import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FaHtml5, FaCss3Alt, FaJs, FaReact, FaPython, FaPhp, FaDatabase,
  FaWordpress, FaShopify, FaGoogle, FaFacebook, FaChartBar, FaRobot,
  FaBrain, FaLaptopCode, FaRocket, FaChartLine, FaFire, FaCog
} from 'react-icons/fa';
import { SiMysql, SiAdobe, SiCanva, SiFigma, SiZapier } from 'react-icons/si';
import './Skills.css';

const Skills = ({ selectedServices = [] }) => {
  // Determine content based on selected services
  const isDigitalMarketing = selectedServices.includes('digital-marketing');
  const isWebDevelopment = selectedServices.includes('web-development');
  const isBothServices = isDigitalMarketing && isWebDevelopment;

  // Dynamic services configuration
  const getServices = () => {
    if (isBothServices) {
      return [
        {
          icon: <FaRocket />,
          title: "Full-Stack Digital Solutions",
          description: "Complete web applications with integrated marketing funnels, analytics tracking, and conversion optimization built-in"
        },
        {
          icon: <FaChartLine />,
          title: "Performance Marketing Campaigns",
          description: "Data-driven ad campaigns on Meta, Google, and LinkedIn with custom landing pages that actually convert visitors"
        },
        {
          icon: <FaLaptopCode />,
          title: "Custom Web Development",
          description: "React applications, WordPress solutions, Shopify stores, and full-stack platforms built for performance and growth"
        },
        {
          icon: <FaBrain />,
          title: "Growth Engineering & Automation",
          description: "Marketing automation workflows, CRM integrations, and growth hacking systems that scale your business 24/7"
        },
        {
          icon: <FaChartBar />,
          title: "Analytics & Conversion Optimization",
          description: "Advanced tracking setups, A/B testing frameworks, and conversion rate optimization for maximum ROI"
        },
        {
          icon: <FaFire />,
          title: "Brand Building & Content Strategy",
          description: "Complete brand ecosystems with strategic content planning, social media growth, and community management"
        }
      ];
    } else if (isDigitalMarketing) {
      return [
        {
          icon: <FaChartLine />,
          title: "Social Media Marketing & Growth",
          description: "Instagram: 27K+ monthly views, LinkedIn strategies, FB + IG Ads, content planning & community management"
        },
        {
          icon: <FaRocket />,
          title: "Paid Advertising Campaigns",
          description: "Meta Ads, Google Ads, LinkedIn Ads - complete campaign setup, optimization, and scaling for maximum ROI"
        },
        {
          icon: <FaBrain />,
          title: "Brand Building & Content Strategy",
          description: "End-to-end brand development: positioning, content strategy, visual identity, viral content creation"
        },
        {
          icon: <FaGoogle />,
          title: "SEO & Content Marketing",
          description: "Keyword research, content optimization, link building, local SEO, and organic growth strategies"
        },
        {
          icon: <FaCog />,
          title: "Marketing Automation & CRM",
          description: "Zapier workflows, email sequences, lead nurturing, customer journey mapping, conversion funnels"
        },
        {
          icon: <FaChartBar />,
          title: "Analytics & Performance Tracking",
          description: "Google Analytics, Facebook Pixel, UTM tracking, ROI analysis, A/B testing, growth optimization"
        }
      ];
    } else if (isWebDevelopment) {
      return [
        {
          icon: <FaReact />,
          title: "Modern Frontend Development",
          description: "React applications, responsive design, modern CSS, performance optimization, mobile-first development"
        },
        {
          icon: <FaDatabase />,
          title: "Full-Stack Web Applications",
          description: "Complete web solutions with React frontend, PHP/Node.js backend, MySQL databases, and API integrations"
        },
        {
          icon: <FaWordpress />,
          title: "WordPress & CMS Solutions",
          description: "Custom WordPress themes, plugin development, e-commerce integration, performance optimization"
        },
        {
          icon: <FaShopify />,
          title: "E-commerce Development",
          description: "Shopify stores, WooCommerce solutions, payment integration, inventory management, conversion optimization"
        },
        {
          icon: <FaCog />,
          title: "Web Performance & Optimization",
          description: "Page speed optimization, SEO-friendly code, security implementations, mobile responsiveness"
        },
        {
          icon: <FaChartBar />,
          title: "Analytics & Tracking Integration",
          description: "Google Analytics setup, conversion tracking, heatmaps, user behavior analysis, performance monitoring"
        }
      ];
    }
    // Fallback to original services
    return [
      {
        icon: <FaChartLine />,
        title: "Social Media Marketing & Growth",
        description: "Instagram: 27K+ monthly views, LinkedIn strategies, FB + IG Ads, content planning & community management"
      },
      {
        icon: <FaRocket />,
        title: "Digital Marketing Strategy",
        description: "Complete campaign strategies: SEO, Paid Ads, Content Marketing, Lead Funnels, Performance Analytics"
      },
      {
        icon: <FaBrain />,
        title: "Brand Building & Management",
        description: "End-to-end brand development: positioning, content strategy, visual identity, audience growth"
      },
      {
        icon: <FaCog />,
        title: "Marketing Automation & CRM",
        description: "Zapier workflows, CRM integration, email sequences, lead nurturing, conversion optimization"
      },
      {
        icon: <FaLaptopCode />,
        title: "Marketing-Focused Web Development",
        description: "Landing pages, conversion-optimized websites, marketing automation tools using React, WordPress, Shopify"
      },
      {
        icon: <FaChartBar />,
        title: "Campaign Analytics & Optimization",
        description: "Performance tracking, ROI analysis, A/B testing, conversion rate optimization, growth hacking"
      }
    ];
  };
  // Dynamic skill categories based on service selection
  const getSkillCategories = () => {
    if (isBothServices) {
      return [{
        title: <><FaBrain className="section-icon" /> My Full-Stack Toolkit</>,
        categories: [
          {
            name: "Development Stack",
            skills: [
              { name: "React", icon: <FaReact />, color: "#61DAFB" },
              { name: "JavaScript", icon: <FaJs />, color: "#F7DF1E" },
              { name: "PHP", icon: <FaPhp />, color: "#777BB4" },
              { name: "MySQL", icon: <SiMysql />, color: "#4479A1" },
              { name: "HTML/CSS", icon: <FaHtml5 />, color: "#E34F26" }
            ]
          },
          {
            name: "Marketing Platforms",
            skills: [
              { name: "Meta Ads", icon: <FaFacebook />, color: "#1877F2" },
              { name: "Google Ads", icon: <FaGoogle />, color: "#4285F4" },
              { name: "Analytics", icon: <FaChartBar />, color: "#FF6B35" },
              { name: "Tag Manager", icon: <FaGoogle />, color: "#246FDB" }
            ]
          },
          {
            name: "CMS & E-commerce",
            skills: [
              { name: "WordPress", icon: <FaWordpress />, color: "#21759B" },
              { name: "Shopify", icon: <FaShopify />, color: "#7AB55C" },
              { name: "WooCommerce", icon: <FaShopify />, color: "#96588A" }
            ]
          },
          {
            name: "Design & Automation",
            skills: [
              { name: "Figma", icon: <SiFigma />, color: "#F24E1E" },
              { name: "Photoshop", icon: <SiAdobe />, color: "#31A8FF" },
              { name: "Zapier", icon: <SiZapier />, color: "#FF4A00" },
              { name: "AI Tools", icon: <FaRobot />, color: "#10A37F" }
            ]
          }
        ]
      }];
    } else if (isDigitalMarketing) {
      return [{
        title: <><FaChartLine className="section-icon" /> My Marketing Arsenal</>,
        categories: [
          {
            name: "Advertising Platforms",
            skills: [
              { name: "Meta Ads", icon: <FaFacebook />, color: "#1877F2" },
              { name: "Google Ads", icon: <FaGoogle />, color: "#4285F4" },
              { name: "LinkedIn Ads", icon: <FaGoogle />, color: "#0077B5" },
              { name: "TikTok Ads", icon: <FaRocket />, color: "#FF0050" }
            ]
          },
          {
            name: "Analytics & Tracking",
            skills: [
              { name: "Google Analytics", icon: <FaChartBar />, color: "#FF6B35" },
              { name: "Facebook Pixel", icon: <FaFacebook />, color: "#1877F2" },
              { name: "Tag Manager", icon: <FaGoogle />, color: "#246FDB" },
              { name: "Hotjar", icon: <FaChartBar />, color: "#FD3A5C" }
            ]
          },
          {
            name: "Content Creation",
            skills: [
              { name: "Canva", icon: <SiCanva />, color: "#00C4CC" },
              { name: "Photoshop", icon: <SiAdobe />, color: "#31A8FF" },
              { name: "Premiere Pro", icon: <SiAdobe />, color: "#9999FF" },
              { name: "Figma", icon: <SiFigma />, color: "#F24E1E" }
            ]
          },
          {
            name: "Marketing Automation",
            skills: [
              { name: "Zapier", icon: <SiZapier />, color: "#FF4A00" },
              { name: "ChatGPT", icon: <FaRobot />, color: "#10A37F" },
              { name: "Make.com", icon: <FaRobot />, color: "#6B46C1" },
              { name: "Mailchimp", icon: <FaRocket />, color: "#FFE01B" }
            ]
          }
        ]
      }];
    } else if (isWebDevelopment) {
      return [{
        title: <><FaLaptopCode className="section-icon" /> My Development Stack</>,
        categories: [
          {
            name: "Frontend Technologies",
            skills: [
              { name: "React", icon: <FaReact />, color: "#61DAFB" },
              { name: "JavaScript", icon: <FaJs />, color: "#F7DF1E" },
              { name: "HTML5", icon: <FaHtml5 />, color: "#E34F26" },
              { name: "CSS3", icon: <FaCss3Alt />, color: "#1572B6" }
            ]
          },
          {
            name: "Backend & Database",
            skills: [
              { name: "PHP", icon: <FaPhp />, color: "#777BB4" },
              { name: "MySQL", icon: <SiMysql />, color: "#4479A1" },
              { name: "Python", icon: <FaPython />, color: "#3776AB" },
              { name: "Node.js", icon: <FaJs />, color: "#339933" }
            ]
          },
          {
            name: "CMS & E-commerce",
            skills: [
              { name: "WordPress", icon: <FaWordpress />, color: "#21759B" },
              { name: "Shopify", icon: <FaShopify />, color: "#7AB55C" },
              { name: "WooCommerce", icon: <FaShopify />, color: "#96588A" },
              { name: "Elementor", icon: <FaWordpress />, color: "#92003B" }
            ]
          },
          {
            name: "Design & Tools",
            skills: [
              { name: "Figma", icon: <SiFigma />, color: "#F24E1E" },
              { name: "Photoshop", icon: <SiAdobe />, color: "#31A8FF" },
              { name: "Git", icon: <FaDatabase />, color: "#F05032" },
              { name: "VS Code", icon: <FaLaptopCode />, color: "#007ACC" }
            ]
          }
        ]
      }];
    }
    // Fallback to original categories
    return [{
      title: <><FaBrain className="section-icon" /> Tech & Tools I Love</>,
      categories: [
        {
          name: "Languages & Frameworks",
          skills: [
            { name: "HTML", icon: <FaHtml5 />, color: "#E34F26" },
            { name: "CSS", icon: <FaCss3Alt />, color: "#1572B6" },
            { name: "JavaScript", icon: <FaJs />, color: "#F7DF1E" },
            { name: "React", icon: <FaReact />, color: "#61DAFB" },
            { name: "Python", icon: <FaPython />, color: "#3776AB" },
            { name: "PHP", icon: <FaPhp />, color: "#777BB4" },
            { name: "MySQL", icon: <SiMysql />, color: "#4479A1" }
          ]
        },
        {
          name: "Marketing Stack",
          skills: [
            { name: "Meta Ads", icon: <FaFacebook />, color: "#1877F2" },
            { name: "Google Ads", icon: <FaGoogle />, color: "#4285F4" },
            { name: "Analytics", icon: <FaChartBar />, color: "#FF6B35" },
            { name: "Tag Manager", icon: <FaGoogle />, color: "#246FDB" }
          ]
        }
      ]
    }];
  };

  const services = getServices();
  const skillCategories = getSkillCategories();

  // SEO content based on selected services
  const getSEOContent = () => {
    if (isBothServices) {
      return {
        title: "Skills & Services - Full-Stack Digital Solutions | Harish Kumar",
        description: "Explore Harish Kumar's comprehensive skills in web development and digital marketing. Full-stack development, React, PHP, Meta Ads, Google Ads, and growth engineering.",
        keywords: "full-stack skills, web development services, digital marketing services, React developer, PHP development, Meta ads expert, growth engineering"
      };
    } else if (isDigitalMarketing) {
      return {
        title: "Digital Marketing Skills & Services | Harish Kumar - Marketing Expert",
        description: "Discover Harish Kumar's digital marketing expertise: Meta Ads, Google Ads, social media marketing, SEO, brand building, and marketing automation skills.",
        keywords: "digital marketing skills, Meta ads expertise, Google ads services, social media marketing, SEO specialist, brand building services"
      };
    } else if (isWebDevelopment) {
      return {
        title: "Web Development Skills & Services | Harish Kumar - Full-Stack Developer",
        description: "Explore Harish Kumar's web development skills: React, PHP, MySQL, WordPress, Shopify, full-stack applications, and custom web solutions.",
        keywords: "web development skills, React developer, PHP developer, full-stack services, WordPress development, Shopify expert, custom web solutions"
      };
    }
    return {
      title: "Skills & Services | Harish Kumar - Digital Marketing & Web Development",
      description: "Explore Harish Kumar's comprehensive skills in digital marketing and web development. Technical expertise combined with marketing strategy.",
      keywords: "digital marketing skills, web development services, technical expertise, marketing strategy, comprehensive solutions"
    };
  };

  const seoContent = getSEOContent();

  return (
    <>
      <Helmet>
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <meta name="keywords" content={seoContent.keywords} />
        <meta property="og:title" content={seoContent.title} />
        <meta property="og:description" content={seoContent.description} />
        <meta property="og:url" content="https://portfolioweb.fliphatmedia.com/#skills" />
        <meta name="twitter:title" content={seoContent.title} />
        <meta name="twitter:description" content={seoContent.description} />
        <link rel="canonical" href="https://portfolioweb.fliphatmedia.com/#skills" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": isBothServices ? "Full-Stack Digital Services" : isDigitalMarketing ? "Digital Marketing Services" : "Web Development Services",
            "description": seoContent.description,
            "itemListElement": services.map((service, index) => ({
              "@type": "Service",
              "position": index + 1,
              "name": service.title,
              "description": service.description,
              "provider": {
                "@type": "Person",
                "name": "Harish Kumar"
              }
            }))
          })}
        </script>
      </Helmet>
      
      <section id="skills" className="skills-section section">
      <div className="container">
        <h2 className="section-title"><FaFire className="section-icon" /> What I Do</h2>
        
        {/* Services Grid */}
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="service-icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
            </div>
          ))}
        </div>

        {/* Tech Skills */}
        <div className="tech-skills">
          {skillCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="skills-category">
              <h3 className="category-title">{category.title}</h3>
              
              {category.categories.map((subCategory, subIndex) => (
                <div key={subIndex} className="skills-subcategory">
                  <h4 className="subcategory-title">{subCategory.name}:</h4>
                  <div className="skills-grid">
                    {subCategory.skills.map((skill, skillIndex) => (
                      <div key={skillIndex} className="skill-item">
                        <div className="skill-icon" style={{ color: skill.color }}>
                          {skill.icon}
                        </div>
                        <span className="skill-name">{skill.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
    </>
  );
};

export default Skills;
